import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/anime";

const AUTOPLAY_DELAY = 6500;

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchDeltaX = useRef(0);

  const goTo = (index: number) => {
    const total = heroSlides.length;
    setActive(((index % total) + total) % total);
  };

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % heroSlides.length);
    }, AUTOPLAY_DELAY);
    return () => window.clearInterval(timer);
  }, [paused]);

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
    touchDeltaX.current = 0;
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    touchDeltaX.current = event.touches[0].clientX - touchStartX.current;
  };

  const handleTouchEnd = () => {
    if (Math.abs(touchDeltaX.current) > 50) {
      goTo(active + (touchDeltaX.current < 0 ? 1 : -1));
    }
    touchStartX.current = null;
    touchDeltaX.current = 0;
  };

  const slide = heroSlides[active];

  return (
    <section
      className="relative w-full overflow-hidden bg-foreground-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      aria-roledescription="carousel"
    >
      <div className="relative h-[560px] w-full md:h-[660px]">
        {heroSlides.map((item, index) => (
          <div
            key={item.id}
            className={[
              "absolute inset-0 transition-opacity duration-700",
              index === active ? "opacity-100" : "pointer-events-none opacity-0",
            ].join(" ")}
          >
            <img
              src={item.image}
              alt={`${item.title} 焦点推荐主视觉`}
              title={`${item.title} 动漫推荐`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/92 via-foreground-950/70 to-foreground-950/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/15 to-transparent" />
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-foreground-950/55 to-transparent" />

        <div className="absolute inset-0 flex items-end">
          <div className="mx-auto w-full max-w-page px-4 pb-16 md:px-6 md:pb-24">
            <div key={slide.id} className="max-w-2xl animate-fade-up">
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-500/95 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-background-50">
                <i className="ri-flashlight-fill text-xs" />
                本周焦点
              </span>

              <h1 className="mt-5 font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl lg:text-[3.4rem]">
                {slide.title}
              </h1>

              <p className="mt-4 max-w-xl text-sm leading-7 text-background-100/90 md:text-base">
                {slide.tagline}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-2 text-[11px] text-background-100/85">
                {slide.tags.map((tag) => (
                  <span key={tag} className="rounded-full border border-background-100/35 px-2.5 py-1">
                    {tag}
                  </span>
                ))}
                <span className="flex items-center gap-1 rounded-full bg-background-50/15 px-2.5 py-1">
                  <i className="ri-star-fill text-primary-400" />
                  {slide.rating.toFixed(1)} 分
                </span>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${slide.id}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  查看剧情介绍
                  <i className="ri-arrow-right-line text-base" />
                </Link>
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-100/45 bg-background-50/10 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/20"
                >
                  浏览全部动漫
                  <i className="ri-compass-3-line text-base" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-6 z-10">
          <div className="mx-auto flex w-full max-w-page items-center justify-between px-4 md:px-6">
            <div className="flex items-center gap-2">
              {heroSlides.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  aria-label={`切换到第 ${index + 1} 张推荐`}
                  onClick={() => goTo(index)}
                  className={[
                    "h-1.5 rounded-full transition-all",
                    index === active
                      ? "w-8 bg-primary-500"
                      : "w-3 bg-background-50/45 hover:bg-background-50/75",
                  ].join(" ")}
                />
              ))}
              <span className="ml-2 font-heading text-xs font-semibold text-background-100/85">
                {String(active + 1).padStart(2, "0")}
                <span className="mx-1 text-background-100/45">/</span>
                {String(heroSlides.length).padStart(2, "0")}
              </span>
            </div>

            <div className="hidden items-center gap-2 md:flex">
              <button
                type="button"
                aria-label="上一张"
                onClick={() => goTo(active - 1)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/40 bg-background-50/10 text-lg text-background-50 transition-colors hover:border-primary-400 hover:text-primary-400"
              >
                <i className="ri-arrow-left-line" />
              </button>
              <button
                type="button"
                aria-label="下一张"
                onClick={() => goTo(active + 1)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/40 bg-background-50/10 text-lg text-background-50 transition-colors hover:border-primary-400 hover:text-primary-400"
              >
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}